// frontend/src/pages/ViewRequests.js
// Workshop request queue — chrome/ink, French, sorted worst-first so the
// requests that still need a devis sit at the top.

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getWorkshopRequests, updateRequestStatus } from '../api/requests';
import { useAuth } from '../context/AuthContext';
import useRequestActions from '../hooks/useRequestActions';
import ScheduleRequestModal from '../components/WorkshopDashboard/ScheduleRequestModal';
import API_URL from '../api/baseURL';
import axios from 'axios';
import {
  Page, Panel, Badge, Table, Tr, Td, Button,
  EmptyState, Loading, ErrorNote,
} from '../components/ui/Iparc';

// Backend statuses -> French label + signal tone. Two-colour rule: red is what
// blocks the workshop, amber is waiting on someone else, green is settled.
// Keys match MaintenanceRequest.status. Both 'rejected' and 'declined' are
// listed: the schema enum says 'declined' but rejectRequest writes 'rejected'
// through findByIdAndUpdate, which skips validators — so real rows carry both.
const STATUS = {
  pending:       { label: 'À deviser',   tone: 'danger' },
  accepted:      { label: 'Accepté',     tone: 'ok' },
  confirmed:     { label: 'Confirmé',    tone: 'ok' },
  'in-progress': { label: 'En cours',    tone: 'ok' },
  completed:     { label: 'Terminé',     tone: 'ok' },
  rescheduled:   { label: 'Reprogrammé', tone: 'warn' },
  rejected:      { label: 'Refusé',      tone: 'neutral' },
  declined:      { label: 'Refusé',      tone: 'neutral' },
  cancelled:     { label: 'Annulé',      tone: 'neutral' },
};
const statusOf = (s) => STATUS[s] || { label: s || '—', tone: 'neutral' };

const EMPTY = [];
const OPEN = ['pending', 'accepted', 'confirmed', 'in-progress', 'rescheduled'];

const btn = 'h-[26px] px-2.5 text-[11px]';

// What the workshop can do next, per status. Before this, every row past
// 'pending' rendered a dash and the job could not be moved forward at all.
//
// There is no "Reprogrammer" any more: it set status to 'rescheduled' to wait
// on a client confirmation that no shipped screen can give, so jobs parked
// there were stuck. Planifier books the slot outright instead, and also
// rescues rows already sitting in 'rescheduled'.
function RequestActions({ req, busy, onAccept, onReject, onNext, onSchedule, onComplete }) {
  const wrap = (children) => <div className="inline-flex gap-1.5">{children}</div>;

  switch (req.status) {
    case 'pending':
      return wrap(<>
        <Button variant="primary" className={btn} disabled={busy} onClick={onAccept}>
          {busy ? '…' : 'Accepter'}
        </Button>
        <Button className={btn} onClick={onSchedule}>Planifier</Button>
        <Button variant="danger" className={btn} disabled={busy} onClick={onReject}>Refuser</Button>
      </>);

    case 'accepted':
    case 'rescheduled':
      return wrap(<>
        <Button variant="primary" className={btn} onClick={onSchedule}>Planifier</Button>
        <Button variant="danger" className={btn} disabled={busy} onClick={onReject}>Refuser</Button>
      </>);

    case 'confirmed':
      return wrap(<>
        <Button variant="primary" className={btn} disabled={busy} onClick={onNext}>
          {busy ? '…' : 'Démarrer'}
        </Button>
        <Button className={btn} onClick={onSchedule}>Replanifier</Button>
      </>);

    case 'in-progress':
      return wrap(<>
        <Button className={btn} onClick={onNext}>Saisir</Button>
        <Button variant="primary" className={btn} disabled={busy} onClick={onComplete}>
          {busy ? '…' : 'Terminer'}
        </Button>
      </>);

    case 'completed':
      return wrap(
        <Button variant="primary" className={btn} onClick={onNext}>Facturer</Button>
      );

    default: // cancelled, declined, rejected
      return <span className="text-[11px] text-[#5C554C]">—</span>;
  }
}

const ViewRequests = ({ workshopId: workshopIdProp }) => {
  const { token, user } = useAuth();
  // Usable as a standalone route (/workshop/requests) as well as embedded.
  const workshopId = workshopIdProp || user?._id;
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('open');

  // Schedule / start / log / invoice — shared with the dashboard queue so both
  // surfaces take the same next step for a given status.
  const actions = useRequestActions();

  const { data: requests = EMPTY, isLoading, isError, error } = useQuery({
    queryKey: ['maintenance-requests', workshopId],
    queryFn: () => getWorkshopRequests(workshopId, token),
    enabled: !!workshopId && !!token,
  });

  const mutation = useMutation({
    mutationFn: async ({ id, status, vehicleId }) => {
      await updateRequestStatus(id, status, '', token);
      if (status === 'accepted' && vehicleId && user?._id) {
        try {
          await axios.patch(`${API_URL}/vehicles/${vehicleId}/assign-workshop`, {
            workshopId: user._id,
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch (err) {
          console.error('[❌ Error assigning vehicle to workshop]', err);
        }
      }
    },
    onSuccess: () => queryClient.invalidateQueries(['maintenance-requests', workshopId]),
  });

  const pendingCount = useMemo(
    () => requests.filter((r) => r.status === 'pending').length,
    [requests]
  );

  const shown = useMemo(() => {
    const list = filter === 'open'
      ? requests.filter((r) => OPEN.includes(r.status))
      : filter === 'pending'
        ? requests.filter((r) => r.status === 'pending')
        : requests;
    // Worst first: to-quote at the top, then oldest.
    return [...list].sort((a, b) =>
      (Number(b.status === 'pending') - Number(a.status === 'pending')) ||
      (new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt))
    );
  }, [requests, filter]);

  const chips = [
    { key: 'open', label: `Ouvertes · ${requests.filter((r) => OPEN.includes(r.status)).length}` },
    { key: 'pending', label: `À deviser · ${pendingCount}` },
    { key: 'all', label: `Toutes · ${requests.length}` },
  ];

  return (
    <Page
      title="Demandes"
      subtitle="Atelier"
      actions={
        <Button onClick={() => queryClient.invalidateQueries(['maintenance-requests', workshopId])}>
          Actualiser
        </Button>
      }
    >
      {isError && <ErrorNote>{error?.message || 'Erreur de chargement des demandes.'}</ErrorNote>}
      {actions.actionError && <ErrorNote>{actions.actionError}</ErrorNote>}

      <Panel
        title="File de travail"
        bodyClass=""
        action={
          <div className="flex gap-[5px]">
            {chips.map((c) => (
              <button
                key={c.key}
                onClick={() => setFilter(c.key)}
                className={`h-[26px] px-2.5 rounded-[7px] text-[11px] font-medium transition-colors ${
                  filter === c.key ? 'bg-[#241F1B] text-[#F4F1EA]' : 'text-[#6E665C] hover:text-[#C9C3B6]'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        }
      >
        {isLoading ? (
          <Loading label="Chargement des demandes…" />
        ) : requests.length === 0 ? (
          <EmptyState
            title="Aucune demande"
            hint="Les demandes d'entretien envoyées par vos clients apparaîtront ici."
          />
        ) : shown.length === 0 ? (
          <EmptyState title="Aucune demande dans cette vue." />
        ) : (
          <Table head={['Véhicule', 'Service', 'Date', 'Statut', '']} minWidth={820}>
            {shown.map((req) => {
              const st = statusOf(req.status);
              const busy = mutation.isPending && mutation.variables?.id === req._id;
              return (
                <Tr key={req._id}>
                  <Td>
                    <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.02em] text-[#F4F1EA]">
                      {req.vehicleId?.plateNumber || '—'}
                    </span>
                    <span className="block text-[11px] text-[#6E665C] mt-0.5">
                      {req.vehicleId?.brand || req.vehicleId?.make || ''} {req.vehicleId?.model || ''}
                    </span>
                  </Td>
                  <Td>{req.serviceType || '—'}</Td>
                  <Td className="whitespace-nowrap">
                    {req.date ? new Date(req.date).toLocaleDateString('fr-FR') : '—'}
                  </Td>
                  <Td><Badge tone={st.tone}>{st.label}</Badge></Td>
                  <Td className="text-right whitespace-nowrap">
                    <RequestActions
                      req={req}
                      busy={busy || actions.busyId === req._id}
                      onAccept={() => mutation.mutate({
                        id: req._id, status: 'accepted', vehicleId: req.vehicleId?._id,
                      })}
                      onReject={() => mutation.mutate({ id: req._id, status: 'rejected' })}
                      onNext={() => actions.run(req)}
                      onSchedule={() => actions.schedule(req)}
                      onComplete={() => actions.complete(req)}
                    />
                  </Td>
                </Tr>
              );
            })}
          </Table>
        )}
      </Panel>

      <ScheduleRequestModal {...actions.scheduleModal} />
    </Page>
  );
};

export default ViewRequests;
